import { resolveMediaUrl } from '@/lib/media';
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Building2, Users, BarChart3, Inbox, Loader2, Plus, Trash2, Mail,
  Crown, UserPlus, ExternalLink, TrendingUp, MousePointerClick, CalendarCheck, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ===== CREATE ORG FORM =====
const CreateOrgForm = ({ onCreated }) => {
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/org/create`, { name: name.trim() });
      toast.success('Organization created!');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto text-center py-12">
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center mx-auto mb-6">
        <Building2 className="w-8 h-8 text-white" />
      </div>
      <h2 className="text-2xl font-bold text-white mb-2" data-testid="create-org-title">Create Your Organization</h2>
      <p className="text-[#A1A1AA] text-sm mb-8">Manage your team's One Link profiles, track leads and analytics from one place.</p>
      <form onSubmit={create} className="space-y-4">
        <Input data-testid="org-name-input" value={name} onChange={(e) => setName(e.target.value)}
          className="bg-[#121212] border-[#27272A] text-white h-12 text-center text-lg" placeholder="Your Company Name" required />
        <Button data-testid="create-org-btn" type="submit" disabled={loading || !name.trim()}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-500 hover:to-fuchsia-500 text-white font-semibold">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Create Organization'}
        </Button>
      </form>
    </div>
  );
};

// ===== MEMBER CARD =====
const MemberCard = ({ member, isOwner, onRemove }) => (
  <div className="flex items-center gap-3 p-3 bg-[#0a0a0a] border border-[#27272A] rounded-xl hover:border-[#3f3f46] transition-all" data-testid={`member-${member.user_id}`}>
    <div className="w-10 h-10 rounded-full bg-[#1a1a1a] flex-shrink-0 overflow-hidden">
      {member.avatar_url ? (
        <img src={resolveMediaUrl(member.avatar_url)} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#71717A] text-sm font-bold">
          {(member.display_name || member.username || '?')[0]?.toUpperCase()}
        </div>
      )}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-white text-sm font-medium truncate">{member.display_name || member.username}</p>
        {member.role === 'owner' && <Crown className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />}
      </div>
      <p className="text-[#71717A] text-xs truncate">{member.email}</p>
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <span className={`px-2 py-0.5 rounded-md text-xs font-medium ${
        member.role === 'owner' ? 'bg-amber-500/10 text-amber-400' : 'bg-violet-500/10 text-violet-400'
      }`}>{member.role}</span>
      {member.role !== 'owner' && (
        <button data-testid={`remove-member-${member.user_id}`} onClick={() => onRemove(member.user_id)}
          className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#71717A] hover:text-red-400 transition-all">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  </div>
);

// ===== INVITE FORM =====
const InviteForm = ({ onInvited }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [copied, setCopied] = useState(false);

  const invite = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setInviteLink('');
    try {
      const res = await axios.post(`${API}/org/invite`, { email: email.trim() });
      toast.success(res.data.message);
      if (res.data.invite_link) {
        setInviteLink(res.data.invite_link);
      }
      if (res.data.auto_added) {
        setEmail('');
      }
      onInvited();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to invite');
    } finally {
      setLoading(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="space-y-3">
      <form onSubmit={invite} className="flex gap-2">
        <Input data-testid="invite-email-input" type="email" value={email} onChange={(e) => { setEmail(e.target.value); setInviteLink(''); }}
          className="bg-[#121212] border-[#27272A] text-white h-10 text-sm flex-1" placeholder="employee@company.com" required />
        <Button data-testid="invite-btn" type="submit" disabled={loading || !email.trim()}
          className="h-10 px-4 rounded-xl bg-violet-500 hover:bg-violet-600 text-white text-sm font-medium flex-shrink-0">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1" /> Invite</>}
        </Button>
      </form>
      {inviteLink && (
        <div className="flex items-center gap-2 p-3 bg-green-500/5 border border-green-500/20 rounded-xl">
          <div className="flex-1 min-w-0">
            <p className="text-green-400 text-xs font-medium mb-1">Share this link with your employee:</p>
            <p className="text-[#A1A1AA] text-xs font-mono truncate">{inviteLink}</p>
          </div>
          <button data-testid="copy-invite-link" onClick={copyLink}
            className="px-3 py-2 rounded-lg bg-green-500/10 hover:bg-green-500/20 text-green-400 text-xs font-medium flex-shrink-0 transition-all">
            {copied ? 'Copied!' : 'Copy Link'}
          </button>
        </div>
      )}
    </div>
  );
};

// ===== STAT CARD =====
const StatCard = ({ label, value, icon: Icon, color = 'violet' }) => (
  <div className="bg-[#0a0a0a] border border-[#27272A] rounded-xl p-4">
    <div className="flex items-center justify-between mb-2">
      <Icon className={`w-5 h-5 text-${color}-400`} />
    </div>
    <p className="text-2xl font-bold text-white">{value}</p>
    <p className="text-[#71717A] text-xs mt-0.5">{label}</p>
  </div>
);

// ===== MAIN ADMIN PANEL =====
const AdminPanel = () => {
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('members');
  const [analytics, setAnalytics] = useState(null);
  const [leads, setLeads] = useState([]);
  const [loadingAnalytics, setLoadingAnalytics] = useState(false);
  const [loadingLeads, setLoadingLeads] = useState(false);

  const fetchAnalytics = useCallback(async () => {
    setLoadingAnalytics(true);
    try {
      const res = await axios.get(`${API}/org/analytics`);
      setAnalytics(res.data);
    } catch {
      // ignore
    } finally {
      setLoadingAnalytics(false);
    }
  }, []);

  const fetchOrg = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/org`);
      setOrg(res.data.org);
      if (res.data.org) {
        fetchAnalytics();
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [fetchAnalytics]);

  useEffect(() => {
    fetchOrg();
  }, [fetchOrg]);

  const fetchLeads = async () => {
    setLoadingLeads(true);
    try {
      const res = await axios.get(`${API}/org/leads`);
      setLeads(res.data.leads || []);
    } catch {
      // ignore
    } finally {
      setLoadingLeads(false);
    }
  };

  const removeMember = async (userId) => {
    try {
      await axios.delete(`${API}/org/members/${userId}`);
      toast.success('Member removed');
      fetchOrg();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>;
  if (!org) return <CreateOrgForm onCreated={fetchOrg} />;

  const tabs = [
    { id: 'members', label: 'Team', icon: Users },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'leads', label: 'Leads', icon: Inbox },
  ];

  return (
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-white" data-testid="admin-panel-title">{org.name}</h2>
          <p className="text-[#71717A] text-xs">{org.members?.length || 0} team members</p>
        </div>
      </div>

      {/* Quick Stats */}
      {analytics && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          <StatCard label="Team Members" value={analytics.total_members} icon={Users} color="violet" />
          <StatCard label="Total Leads" value={analytics.total_leads} icon={Inbox} color="green" />
          <StatCard label="Total Clicks" value={analytics.total_clicks} icon={MousePointerClick} color="blue" />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0a0a0a] border border-[#27272A] rounded-xl p-1 mb-6">
        {tabs.map((tab) => (
          <button key={tab.id} data-testid={`admin-tab-${tab.id}`}
            onClick={() => { setActiveTab(tab.id); if (tab.id === 'leads' && leads.length === 0) fetchLeads(); }}
            className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab.id ? 'bg-violet-500/20 text-violet-400' : 'text-[#71717A] hover:text-[#A1A1AA]'
            }`}>
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        {/* MEMBERS TAB */}
        {activeTab === 'members' && (
          <motion.div key="members" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-4">
            <div>
              <p className="text-[#EDEDED] text-sm font-medium mb-2">Invite Employee</p>
              <InviteForm onInvited={fetchOrg} />
              <p className="text-[#71717A] text-xs mt-2">They'll receive an email to sign up. If they already have an account, they'll be added instantly.</p>
            </div>
            <div>
              <p className="text-[#EDEDED] text-sm font-medium mb-3">Team Members ({org.members?.length || 0})</p>
              <div className="space-y-2">
                {org.members?.map((m) => (
                  <MemberCard key={m.user_id} member={m} isOwner={org.is_owner} onRemove={removeMember} />
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* ANALYTICS TAB */}
        {activeTab === 'analytics' && (
          <motion.div key="analytics" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-4">
            {loadingAnalytics ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>
            ) : analytics?.member_stats?.length > 0 ? (
              <>
                <p className="text-[#EDEDED] text-sm font-medium">Team Performance</p>
                <div className="space-y-2">
                  {analytics.member_stats.map((ms, i) => (
                    <div key={ms.user_id} className="flex items-center gap-3 p-3 bg-[#0a0a0a] border border-[#27272A] rounded-xl">
                      <span className="text-[#71717A] text-sm font-bold w-6">{i + 1}</span>
                      <div className="w-8 h-8 rounded-full bg-[#1a1a1a] overflow-hidden flex-shrink-0">
                        {ms.avatar_url ? (
                          <img src={resolveMediaUrl(ms.avatar_url)} alt="" className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#71717A] text-xs font-bold">
                            {(ms.display_name || '?')[0]?.toUpperCase()}
                          </div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm font-medium truncate">{ms.display_name || ms.username}</p>
                        <p className="text-[#71717A] text-xs">@{ms.username}</p>
                      </div>
                      <div className="flex items-center gap-4 text-xs flex-shrink-0">
                        <div className="text-center">
                          <p className="text-green-400 font-bold">{ms.total_leads}</p>
                          <p className="text-[#71717A]">Leads</p>
                        </div>
                        <div className="text-center">
                          <p className="text-blue-400 font-bold">{ms.total_clicks}</p>
                          <p className="text-[#71717A]">Clicks</p>
                        </div>
                        <div className="text-center">
                          <p className="text-violet-400 font-bold">{ms.total_bookings}</p>
                          <p className="text-[#71717A]">Bookings</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-[#71717A] text-sm text-center py-8">No analytics data yet. Add team members to start tracking.</p>
            )}
          </motion.div>
        )}

        {/* LEADS TAB */}
        {activeTab === 'leads' && (
          <motion.div key="leads" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} className="space-y-4">
            {loadingLeads ? (
              <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-violet-400" /></div>
            ) : leads.length > 0 ? (
              <>
                <p className="text-[#EDEDED] text-sm font-medium">All Team Leads ({leads.length})</p>
                <div className="space-y-2 max-h-[500px] overflow-y-auto pr-1">
                  {leads.map((lead) => (
                    <div key={lead.id} className="p-3 bg-[#0a0a0a] border border-[#27272A] rounded-xl">
                      <div className="flex items-center justify-between mb-1">
                        <p className="text-white text-sm font-medium">{lead.name || 'Anonymous'}</p>
                        <span className="text-xs text-violet-400 bg-violet-500/10 px-2 py-0.5 rounded-md">{lead.employee_name}</span>
                      </div>
                      <p className="text-[#A1A1AA] text-xs">{lead.email}</p>
                      {lead.message && <p className="text-[#71717A] text-xs mt-1 line-clamp-2">{lead.message}</p>}
                      <p className="text-[#52525B] text-xs mt-1">{lead.created_at?.slice(0, 10)}</p>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="text-center py-8">
                <Inbox className="w-10 h-10 text-[#3f3f46] mx-auto mb-3" />
                <p className="text-[#71717A] text-sm">No leads yet across your team. Leads from all employee profiles will show up here.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminPanel;
